/**
 * Generic helpers for "where did the user come from" breadcrumbs.
 *
 * Interior detail pages that can be reached from several list pages root their
 * breadcrumb on the originating page. That page's URL travels in a `from` query
 * param; the detail page resolves it against a domain-specific matcher table to
 * pick the root crumb's label, and uses the `from` value itself as the root href
 * (so it returns to the exact list, filters and all). Display names come from the
 * destination page's own data, never from the URL.
 */

export const BREADCRUMB_FROM_PARAM = "from";
/** Explicit root-crumb label for single-entity origins (e.g. a specific review or meeting). */
export const BREADCRUMB_FROM_LABEL_PARAM = "fromLabel";

/** Only honor internal absolute paths; reject protocol-relative/external URLs. */
const sanitizeFrom = (value: string): string => (value.startsWith("/") && !value.startsWith("//") ? value : "");

export const getSanitizedFrom = (search: string): string =>
	sanitizeFrom(new URLSearchParams(search).get(BREADCRUMB_FROM_PARAM) ?? "");

/** Read the explicit root-crumb label, if the origin link supplied one. */
export const getFromLabel = (search: string): string =>
	new URLSearchParams(search).get(BREADCRUMB_FROM_LABEL_PARAM) ?? "";

/** Append the current page URL as `from` (and an optional explicit root `label`) on an interior href. */
export const appendCurrentLocationAsFrom = (href: string, label?: string): string => {
	const origin = typeof window !== "undefined" ? window.location.pathname + window.location.search : "";
	if (!origin) return href;
	const separator = href.includes("?") ? "&" : "?";
	let result = `${href}${separator}${BREADCRUMB_FROM_PARAM}=${encodeURIComponent(origin)}`;
	if (label) result += `&${BREADCRUMB_FROM_LABEL_PARAM}=${encodeURIComponent(label)}`;
	return result;
};

/**
 * Resolve the sanitized `from` and the first matcher whose `match` substring it contains.
 * Matchers must be ordered most-specific-first. Returns `matcher: null` when `from` is
 * absent/invalid or nothing matches.
 */
export const resolveOrigin = <T extends { match: string }>(
	search: string,
	matchers: readonly T[]
): { from: string; matcher: T | null } => {
	const from = getSanitizedFrom(search);
	if (!from) return { from: "", matcher: null };
	return { from, matcher: matchers.find(({ match }) => from.includes(match)) ?? null };
};
